import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "wo_auth_token";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [permissions, setPermissions] = useState({});

  // Attach token to axios defaults whenever it changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      delete axios.defaults.headers.common["Authorization"];
      localStorage.removeItem(TOKEN_KEY);
    }
  }, [token]);

  // Fetch current user + permissions on mount if we have a token
  useEffect(() => {
    const bootstrap = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const [meRes, permRes] = await Promise.all([
          axios.get(`${API}/auth/me`),
          axios.get(`${API}/me/permissions`).catch(() => ({ data: { permissions: {} } })),
        ]);
        setUser(meRes.data);
        setPermissions(permRes.data.permissions || {});
      } catch (e) {
        setToken(null);
        setUser(null);
        setPermissions({});
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
  }, [token]);

  // Global 401 interceptor to force logout
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err.response?.status === 401 && token) {
          setToken(null);
          setUser(null);
          setPermissions({});
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [token]);

  const login = async (username, password) => {
    const res = await axios.post(`${API}/auth/login`, { username, password });
    setToken(res.data.access_token);
    setUser(res.data.user);
    // Immediately fetch permissions with the new token
    try {
      const permRes = await axios.get(`${API}/me/permissions`, {
        headers: { Authorization: `Bearer ${res.data.access_token}` },
      });
      setPermissions(permRes.data.permissions || {});
    } catch {
      setPermissions({});
    }
    return res.data.user;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setPermissions({});
  };

  const refreshPermissions = async () => {
    try {
      const res = await axios.get(`${API}/me/permissions`);
      setPermissions(res.data.permissions || {});
    } catch {
      /* ignore */
    }
  };

  const can = (permKey) => !!permissions[permKey];

  return (
    <AuthContext.Provider
      value={{ user, loading, login, logout, isAuthenticated: !!user, permissions, can, refreshPermissions }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
